/**
 * Colour theme: the user's PREFERENCE is `system | dark | light`; the document always carries the
 * RESOLVED theme (`<html data-theme="dark|light">`) which `app/styles/tokens.css` keys off, plus
 * the preference itself (`data-theme-pref`). `public/theme-init.js` sets both before the first
 * paint from the copy kept in localStorage, so a reload never flashes the wrong theme; the viewer's
 * account value (the source of truth, synced across devices) replaces it as soon as it is known.
 *
 * Board / column / card looks are NOT part of this: they are stored colours that the theme never
 * rewrites.
 */
export type ThemePreference = 'system' | 'dark' | 'light';
export type ResolvedTheme = 'dark' | 'light';

export const THEME_STORAGE_KEY = 'zadok.theme';
export const THEME_PREFERENCES: readonly ThemePreference[] = ['system', 'dark', 'light'];

export function isThemePreference(value: unknown): value is ThemePreference {
  return value === 'system' || value === 'dark' || value === 'light';
}

export function systemPrefersDark(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );
}

export function resolveTheme(preference: ThemePreference, prefersDark: boolean): ResolvedTheme {
  if (preference === 'system') return prefersDark ? 'dark' : 'light';
  return preference;
}

export function storedPreference(): ThemePreference {
  try {
    const value = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isThemePreference(value) ? value : 'system';
  } catch {
    return 'system';
  }
}

/** Put the theme on the document (and remember the preference for the next first paint). */
export function applyTheme(
  preference: ThemePreference,
  root: HTMLElement = document.documentElement,
): ResolvedTheme {
  const resolved = resolveTheme(preference, systemPrefersDark());
  root.dataset['theme'] = resolved;
  root.dataset['themePref'] = preference;
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    /* private mode: the account value still applies after load */
  }
  return resolved;
}

/** The theme currently shown (for code that needs to pick a palette, e.g. contrast previews). */
export function currentTheme(root: HTMLElement = document.documentElement): ResolvedTheme {
  const value = root.dataset['theme'];
  if (value === 'dark' || value === 'light') return value;
  return systemPrefersDark() ? 'dark' : 'light';
}
